import { Spine } from "@esotericsoftware/spine-pixi-v7";
import * as PIXI from "pixi.js";
import anime from "animejs";
import TickerTimeout from "../../../utils/TickerTimeout";
import {
  setShowMaxWin,
  setSceneTransitionActive,
} from "../../../State/actions";
import { formatToCustomLocale } from "../../../utilities";
import { Settings } from "../../../settings";
import { PixiSettings } from "../../../pixiSettings";
import { SOUND_NAME } from "../../../sounds";
import { soundManager } from "../../../SoundManager";

/**
 * MaxWinDialogController - Handles max win dialog animation sequence
 * Requires manual user interaction to close (button click or overlay click)
 * Shows when player reaches max win threshold (bet × 5000)
 */
class MaxWinDialogController {
  constructor(
    container,
    dimensionsContext,
    dispatch,
    symbolHeight,
    overlayRef,
    buttonRef,
    t
  ) {
    this.container = container;
    this.dimensionsContext = dimensionsContext;
    this.dispatch = dispatch;
    this.symbolHeight = symbolHeight;
    this.overlayRef = overlayRef;
    this.buttonRef = buttonRef;
    this.t = t;

    // Extract dimensions object for backward compatibility
    this.dimensions = dimensionsContext.dimensions;

    // Spine instance
    this.maxWinSpine = null;
    this.isPlaying = false;
    this.isVisible = false;
    this.initialized = false;
    this.isWaitingForClick = false;

    // Counter attached to spine bone
    this.counterContainer = null;
    this.counterBitmapTexts = [];
    this.currentCounterValue = 0;
    this.counterAnimationRef = null;
    this.boneUpdateTicker = null;
    this.winValue = 0; // Store win value from state
    this.betValue = 0; // Store bet value from state

    // Multiplier text attached to spine bone (e.g., "5000x BET")
    this.multiplierContainer = null;
    this.multiplierText = null;
    this.currentMultiplierValue = 0;
    this.multiplierAnimationRef = null;

    // Button attached to spine bone
    this.buttonContainer = null;
    this.buttonText = null;

    // User interaction state
    this.canSkipAnimation = false;
    this.hasHandledSkipClick = false;
    this.isHandlingUserClick = false;
    this.isClosing = false;
    this.isFinishing = false;
    this.debounceTimeout = null;
    this.skipProtectionTimeout = null;

    // Internal caches for performance optimization
    this._deviceScaleMultiplierCache = null;
    this._fontSizeMultiplierCache = null;
    this._buttonYOffsetCache = null;
    this._availableAnimationsCache = null;
    this._lastDevice = null;
    this._lastOrientation = null;

    this.initializeMaxWinSpine();
  }

  /**
   * Apply proportional scaling and centering to spine animation
   * Scales based on symbol size and device type, positions relative to screen center
   */
  applyScaleAndCenter() {
    if (!this.maxWinSpine || !this.dimensions) return;

    // Proportional scaling based on symbol size
    const nativeSize = Settings.SYMBOL_HEIGHT;
    const targetSize = this.symbolHeight || nativeSize;
    let scale = Math.max(0.1, Math.min(targetSize / nativeSize, 2));

    // Apply device/orientation-specific multipliers
    const deviceMultiplier = this.getDeviceScaleMultiplier();
    scale *= deviceMultiplier;

    this.maxWinSpine.scale.set(scale);
    // Position relative to centered parent container (0,0 is now screen center)
    this.maxWinSpine.x = 0;
    this.maxWinSpine.y = -(this.symbolHeight / 2 || 0);
  }

  /**
   * Get device-specific scale multiplier for spine animations
   * Cached for performance - invalidated when device/orientation changes
   */
  getDeviceScaleMultiplier() {
    const { device, orientation } = this.dimensionsContext;

    // Check cache validity
    if (
      this._deviceScaleMultiplierCache !== null &&
      this._lastDevice === device &&
      this._lastOrientation === orientation
    ) {
      return this._deviceScaleMultiplierCache;
    }

    // All devices use the same multiplier (1.0)
    const multiplier = 1.0;

    // Cache result
    this._deviceScaleMultiplierCache = multiplier;
    this._lastDevice = device;
    this._lastOrientation = orientation;

    return multiplier;
  }

  /**
   * Get device-specific font size multiplier for button text
   * Cached for performance - invalidated when device/orientation changes
   */
  getFontSizeMultiplier() {
    const { device, orientation } = this.dimensionsContext;

    // Check cache validity
    if (
      this._fontSizeMultiplierCache !== null &&
      this._lastDevice === device &&
      this._lastOrientation === orientation
    ) {
      return this._fontSizeMultiplierCache;
    }

    // All devices use the same multiplier (1.0)
    const multiplier = 1.0;

    // Cache result
    this._fontSizeMultiplierCache = multiplier;
    this._lastDevice = device;
    this._lastOrientation = orientation;

    return multiplier;
  }

  /**
   * Get device-specific Y offset for button positioning
   * Adjusts button position based on device and orientation
   * Matches WinAnimationController, HoldAndWinIntroController, and HoldAndWinReloadController button offset values
   * Cached for performance - invalidated when device/orientation changes
   */
  getButtonYOffset() {
    const { device, orientation } = this.dimensionsContext;

    // Check cache validity
    if (
      this._buttonYOffsetCache !== null &&
      this._lastDevice === device &&
      this._lastOrientation === orientation
    ) {
      return this._buttonYOffsetCache;
    }

    let offset;
    if (device === PixiSettings.DEVICE.MOBILE) {
      if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        offset = 225; // Mobile portrait offset
      } else {
        offset = 30; // Mobile landscape offset
      }
    } else if (device === PixiSettings.DEVICE.TABLET) {
      if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        offset = 185; // Tablet portrait offset
      } else {
        offset = 90; // Tablet landscape offset
      }
    } else {
      offset = 0; // Desktop offset
    }

    // Cache result
    this._buttonYOffsetCache = offset;
    this._lastDevice = device;
    this._lastOrientation = orientation;

    return offset;
  }

  /**
   * Update dimensions and symbol sizes, then recalculate scale and position
   */
  updateDimensions(dimensionsContext, symbolHeight) {
    // Check if any values actually changed to avoid unnecessary recalculations
    const oldDimensions = this.dimensionsContext?.dimensions;
    const newDimensions = dimensionsContext?.dimensions;
    const oldDevice = this.dimensionsContext?.device;
    const newDevice = dimensionsContext?.device;

    const dimensionsChanged =
      !oldDimensions ||
      oldDimensions.width !== newDimensions?.width ||
      oldDimensions.height !== newDimensions?.height ||
      this.dimensionsContext?.device !== dimensionsContext?.device ||
      this.dimensionsContext?.orientation !== dimensionsContext?.orientation;

    const symbolHeightChanged = this.symbolHeight !== symbolHeight;
    const deviceChanged = oldDevice !== newDevice;

    // Only proceed if something actually changed
    if (!dimensionsChanged && !symbolHeightChanged) {
      return;
    }

    this.dimensionsContext = dimensionsContext;
    this.dimensions = dimensionsContext.dimensions;
    this.symbolHeight = symbolHeight;

    // Invalidate caches when device/orientation changes
    if (
      deviceChanged ||
      this._lastDevice !== dimensionsContext.device ||
      this._lastOrientation !== dimensionsContext.orientation
    ) {
      this._deviceScaleMultiplierCache = null;
      this._fontSizeMultiplierCache = null;
      this._buttonYOffsetCache = null;
      this._lastDevice = dimensionsContext.device;
      this._lastOrientation = dimensionsContext.orientation;
    }

    // Update button text if device changed (tap vs click)
    if (deviceChanged && this.buttonText) {
      this.updateButtonText();
    }

    // Always update spine positioning whenever dimensions change
    if (
      this.maxWinSpine &&
      this.dimensions &&
      (dimensionsChanged || symbolHeightChanged)
    ) {
      if (this.symbolHeight && this.symbolHeight > 0) {
        this.applyScaleAndCenter();
      }
    }
  }

  /**
   * Initialize the max win spine
   */
  initializeMaxWinSpine() {
    try {
      this.maxWinSpine = this.createMaxWinSpine();

      if (!this.maxWinSpine) {
        return;
      }

      const missingComponents = [];
      if (!this.maxWinSpine.skeleton) missingComponents.push("skeleton");
      if (!this.maxWinSpine.state) missingComponents.push("state");
      if (!this.maxWinSpine.skeleton?.data)
        missingComponents.push("skeleton.data");

      if (missingComponents.length > 0) {
        return;
      }

      this.container.addChild(this.maxWinSpine);
      this.maxWinSpine.alpha = 0.001;
      this.maxWinSpine.zIndex = 20;

      this.applyScaleAndCenter();

      if (this.maxWinSpine.parent !== this.container) {
        return;
      }

      this.initialized = true;

      // Invalidate animations cache when spine is reinitialized
      this._availableAnimationsCache = null;

      this.warmUpMaxWinSpine();

      this.initializeCounter(this.winValue);
      this.initializeMultiplier();
      this.initializeButton();
    } catch (error) {
      // Silent fail
    }
  }

  /**
   * Create max win spine from loaded assets
   */
  createMaxWinSpine() {
    try {
      const assets = PIXI.Assets.cache;
      const skeletonAlias = "max_win-skeleton";
      const atlasAlias = "max_win-atlas";

      if (!assets.has(skeletonAlias) || !assets.has(atlasAlias)) {
        return null;
      }

      const maxWinSpine = Spine.from({
        skeleton: skeletonAlias,
        atlas: atlasAlias,
      });

      if (!maxWinSpine?.skeleton?.data?.animations) {
        return null;
      }

      return maxWinSpine;
    } catch (error) {
      return null;
    }
  }

  /**
   * Initialize skeleton by setting an animation to populate spine structure
   */
  warmUpMaxWinSpine() {
    if (!this.maxWinSpine?.state) return;

    try {
      const animations = this.getAvailableAnimations();
      if (animations.length > 0) {
        this.maxWinSpine.state.setAnimation(0, animations[0], false);
        this.maxWinSpine.state.update(0.016);
        this.maxWinSpine.skeleton.updateWorldTransform();
      }
      this.maxWinSpine.alpha = 0.001;
    } catch {
      // Silent fail
    }
  }

  /**
   * Get available animations from the spine
   * Cached for performance - invalidated when spine is reinitialized
   */
  getAvailableAnimations() {
    if (!this.maxWinSpine?.skeleton?.data?.animations) return [];

    // Check cache validity
    if (this._availableAnimationsCache !== null) {
      return this._availableAnimationsCache;
    }

    // Calculate and cache
    const animations = this.maxWinSpine.skeleton.data.animations.map(
      (a) => a.name
    );
    this._availableAnimationsCache = animations;

    return animations;
  }

  /**
   * Initialize counter and attach it to spine's "text_value_spacer" bone
   */
  initializeCounter(winValue = 0) {
    if (!this.maxWinSpine || !this.maxWinSpine.skeleton) {
      return;
    }

    // Create counter container as child of spine
    this.counterContainer = new PIXI.Container();
    this.counterContainer.name = "max_win_counter_attached_to_bone";
    this.counterContainer.alpha = 0.001;
    this.counterContainer.zIndex = 1000; // Render above spine effects

    this.maxWinSpine.addChild(this.counterContainer);
    this.maxWinSpine.sortableChildren = true;

    // Initialize with win value from state
    this.updateCounterDisplay(winValue);
  }

  /**
   * Update counter display with new value
   */
  updateCounterDisplay(value) {
    if (!this.counterContainer) return;

    // Clear existing text
    this.counterBitmapTexts.forEach((text) => text.destroy());
    this.counterBitmapTexts = [];
    this.counterContainer.removeChildren();

    // Format value
    const formattedValue = formatToCustomLocale(Math.floor(value));
    const formattedText = formattedValue;

    // Counter area dimensions in spine's natural size
    const availableWidth = 788;
    const availableHeight = 200;

    // Start with ideal font size
    const idealFontSize = Math.round(availableHeight * 0.45);

    // Create single BitmapText with the full formatted text
    const bitmapText = new PIXI.BitmapText(formattedText, {
      fontName: "font",
      fontSize: idealFontSize,
    });

    // Center the text
    bitmapText.anchor.set(0.5);
    bitmapText.x = 0;
    bitmapText.y = 0;

    // Scale down if text exceeds 90% of available width
    const textWidth = bitmapText.width;
    if (textWidth > availableWidth * 0.9) {
      const scale = (availableWidth * 0.9) / textWidth;
      bitmapText.scale.set(scale);
    }

    this.counterContainer.addChild(bitmapText);
    this.counterBitmapTexts.push(bitmapText);
  }

  /**
   * Sync counter transform with spine's "text_value_spacer" bone
   */
  updateCounterFromBone() {
    if (
      !this.maxWinSpine ||
      !this.counterContainer ||
      !this.maxWinSpine.skeleton
    ) {
      return;
    }

    const valueBone = this.maxWinSpine.skeleton.findBone("text_value_spacer");
    if (!valueBone) {
      return;
    }

    // Apply bone's world transform to counter
    this.counterContainer.x = valueBone.worldX;
    this.counterContainer.y = valueBone.worldY;
    this.counterContainer.rotation =
      (valueBone.getWorldRotationX() * Math.PI) / 180;
    this.counterContainer.scale.set(
      valueBone.getWorldScaleX(),
      valueBone.getWorldScaleY()
    );
  }

  /**
   * Initialize button and attach it to spine's "text_tap_to_contiune" bone
   */
  initializeButton() {
    if (!this.maxWinSpine || !this.maxWinSpine.skeleton) {
      return;
    }

    const buttonBone = this.maxWinSpine.skeleton.findBone(
      "text_tap_to_contiune"
    );
    if (!buttonBone) {
      return;
    }

    // Create button container as child of spine
    this.buttonContainer = new PIXI.Container();
    this.buttonContainer.name = "max_win_continue_button_attached_to_bone";
    this.buttonContainer.alpha = 0.001;
    this.buttonContainer.zIndex = 1001; // Render above counter

    // Get translated button text based on device
    // ── One word, every device: TAP ──
    //
    // This used to say CLICK on desktop. Nothing else in the game did: the
    // intro popup says TAP TO START, the big win says TAP TO CONTINUE
    // ("Always 'TAP TO CONTINUE' regardless of device", in its own words), and
    // Pot Collection's screens say TAP. So the free-spins dialogs were the only
    // ones switching, which made the two screens that bookend the whole feature
    // disagree with the screens either side of them.
    const buttonText = this.t("tap_to_continue").toUpperCase();

    // Calculate font size with device-specific multiplier
    const baseFontSize = 70;
    const fontMultiplier = this.getFontSizeMultiplier();
    const finalFontSize = baseFontSize * fontMultiplier;

    // Create text style
    const textStyle = new PIXI.TextStyle({
      fontFamily: "Noto Sans, Arial, sans-serif",
      fontSize: finalFontSize,
      fontWeight: "700",
      fill: "#ffffff",
      dropShadow: true,
      dropShadowColor: "#000000",
      dropShadowDistance: 4,
      dropShadowBlur: 4,
      dropShadowAlpha: 0.8,
      align: "center",
    });

    // Create PIXI.Text
    this.buttonText = new PIXI.Text(buttonText, textStyle);
    this.buttonText.anchor.set(0.5);
    this.buttonText.x = 0;
    this.buttonText.y = 0;

    // Scale down the button (0.7x smaller)
    this.buttonText.scale.set(0.7);

    this.buttonContainer.addChild(this.buttonText);
    this.maxWinSpine.addChild(this.buttonContainer);
    this.maxWinSpine.sortableChildren = true;
  }

  /**
   * Sync button transform with spine's "text_tap_to_contiune" bone
   * Applies device-specific Y offset for responsive positioning
   */
  updateButtonFromBone() {
    if (
      !this.maxWinSpine ||
      !this.buttonContainer ||
      !this.maxWinSpine.skeleton
    ) {
      return;
    }

    const buttonBone = this.maxWinSpine.skeleton.findBone(
      "text_tap_to_contiune"
    );
    if (!buttonBone) {
      return;
    }

    // Get device-specific Y offset
    const yOffset = this.getButtonYOffset();

    // Apply bone's world transform to button with device-specific Y offset
    this.buttonContainer.x = buttonBone.worldX;
    this.buttonContainer.y = buttonBone.worldY + yOffset;
    this.buttonContainer.rotation =
      (buttonBone.getWorldRotationX() * Math.PI) / 180;
    this.buttonContainer.scale.set(
      buttonBone.getWorldScaleX(),
      buttonBone.getWorldScaleY()
    );
  }

  /**
   * Show counter
   */
  showCounter() {
    if (!this.counterContainer) return;
    this.counterContainer.alpha = 1;
  }

  /**
   * Hide counter
   */
  hideCounter() {
    if (!this.counterContainer) return;
    this.counterContainer.alpha = 0.001;
  }

  /**
   * Initialize multiplier text and attach it to spine's "text_x_bet_spacer" bone
   */
  initializeMultiplier() {
    if (!this.maxWinSpine || !this.maxWinSpine.skeleton) {
      return;
    }

    const multiplierBone =
      this.maxWinSpine.skeleton.findBone("text_x_bet_spacer");
    if (!multiplierBone) {
      return;
    }

    // Create multiplier container as child of spine
    this.multiplierContainer = new PIXI.Container();
    this.multiplierContainer.name = "max_win_multiplier_attached_to_bone";
    this.multiplierContainer.alpha = 0.001;
    this.multiplierContainer.zIndex = 999; // Render below counter

    // Get translated "BET" text
    const betText = this.t("bet").toUpperCase();

    // Create text style
    const textStyle = new PIXI.TextStyle({
      fontFamily: "Noto Sans, Arial, sans-serif",
      fontSize: 48,
      fontWeight: "700",
      fill: "#ffffff",
      dropShadow: true,
      dropShadowColor: "#000000",
      dropShadowDistance: 3,
      dropShadowBlur: 3,
      dropShadowAlpha: 0.6,
      align: "center",
    });

    // Create PIXI.Text (will be updated with actual multiplier when animation starts)
    this.multiplierText = new PIXI.Text(`0x ${betText}`, textStyle);
    this.multiplierText.anchor.set(0.5);
    this.multiplierText.x = 0;
    this.multiplierText.y = 0;

    this.multiplierContainer.addChild(this.multiplierText);
    this.maxWinSpine.addChild(this.multiplierContainer);
    this.maxWinSpine.sortableChildren = true;
  }

  /**
   * Update multiplier text display
   */
  updateMultiplierDisplay(multiplierValue) {
    if (!this.multiplierText) return;

    const multiplier = Math.floor(multiplierValue);
    const betText = this.t("bet").toUpperCase();
    this.multiplierText.text = `${multiplier}x ${betText}`;
  }

  /**
   * Animate multiplier value from one number to another
   */
  startMultiplierAnimation(fromValue, toValue, duration) {
    if (this.multiplierAnimationRef) {
      this.multiplierAnimationRef.pause();
      this.multiplierAnimationRef = null;
    }

    this.currentMultiplierValue = fromValue;
    this.updateMultiplierDisplay(fromValue);

    this.multiplierAnimationRef = anime({
      targets: this,
      currentMultiplierValue: toValue,
      duration: duration,
      easing: "easeOutQuad",
      update: () => {
        this.updateMultiplierDisplay(this.currentMultiplierValue);
      },
      complete: () => {
        this.currentMultiplierValue = toValue;
        this.updateMultiplierDisplay(toValue);
        this.multiplierAnimationRef = null;
      },
    });
  }

  /**
   * Instantly set multiplier to specific value
   */
  jumpMultiplierToValue(value) {
    if (this.multiplierAnimationRef) {
      this.multiplierAnimationRef.pause();
      this.multiplierAnimationRef = null;
    }

    this.currentMultiplierValue = value;
    this.updateMultiplierDisplay(value);
  }

  /**
   * Reset multiplier to zero
   */
  resetMultiplier() {
    if (this.multiplierAnimationRef) {
      this.multiplierAnimationRef.pause();
      this.multiplierAnimationRef = null;
    }

    this.currentMultiplierValue = 0;
    this.updateMultiplierDisplay(0);
  }

  /**
   * Sync multiplier transform with spine's "text_x_bet_spacer" bone
   */
  updateMultiplierFromBone() {
    if (
      !this.maxWinSpine ||
      !this.multiplierContainer ||
      !this.maxWinSpine.skeleton
    ) {
      return;
    }

    const multiplierBone =
      this.maxWinSpine.skeleton.findBone("text_x_bet_spacer");
    if (!multiplierBone) {
      return;
    }

    // Apply bone's world transform to multiplier
    this.multiplierContainer.x = multiplierBone.worldX;
    this.multiplierContainer.y = multiplierBone.worldY;
    this.multiplierContainer.rotation =
      (multiplierBone.getWorldRotationX() * Math.PI) / 180;
    this.multiplierContainer.scale.set(
      multiplierBone.getWorldScaleX(),
      multiplierBone.getWorldScaleY()
    );
  }

  /**
   * Show multiplier
   */
  showMultiplier() {
    if (!this.multiplierContainer) return;
    this.multiplierContainer.alpha = 1;
  }

  /**
   * Hide multiplier
   */
  hideMultiplier() {
    if (!this.multiplierContainer) return;
    this.multiplierContainer.alpha = 0.001;
  }

  /**
   * Animate counter value from one number to another
   */
  startCounterAnimation(fromValue, toValue, duration, onComplete) {
    if (this.counterAnimationRef) {
      this.counterAnimationRef.pause();
      this.counterAnimationRef = null;
    }

    this.currentCounterValue = fromValue;
    this.updateCounterDisplay(fromValue);

    // Start counter sound
    soundManager.play(SOUND_NAME.counter);

    this.counterAnimationRef = anime({
      targets: this,
      currentCounterValue: toValue,
      duration: duration,
      easing: "easeOutQuad",
      update: () => {
        this.updateCounterDisplay(this.currentCounterValue);
      },
      complete: () => {
        this.currentCounterValue = toValue;
        this.updateCounterDisplay(toValue);
        this.counterAnimationRef = null;

        // Stop counter sound
        soundManager.stop(SOUND_NAME.counter);

        // Call completion callback
        if (onComplete && typeof onComplete === "function") {
          onComplete();
        }
      },
    });
  }

  /**
   * Instantly set counter to specific value
   */
  jumpCounterToValue(value) {
    if (this.counterAnimationRef) {
      this.counterAnimationRef.pause();
      this.counterAnimationRef = null;
    }

    this.currentCounterValue = value;
    this.updateCounterDisplay(value);
    soundManager.stop(SOUND_NAME.counter);
  }

  /**
   * Reset counter to zero
   */
  resetCounter() {
    if (this.counterAnimationRef) {
      this.counterAnimationRef.pause();
      this.counterAnimationRef = null;
    }

    this.currentCounterValue = 0;
    this.updateCounterDisplay(0);
    soundManager.stop(SOUND_NAME.counter);
  }

  /**
   * Check if counter value is currently animating
   */
  isCounterAnimating() {
    return this.counterAnimationRef !== null;
  }

  /**
   * Get current counter value
   */
  getCurrentCounterValue() {
    return this.currentCounterValue || 0;
  }

  /**
   * Start syncing counter, multiplier, and button with spine bone every frame
   */
  startBoneUpdateLoop() {
    if (!this.maxWinSpine || !this.counterContainer) return;

    if (!this.boneUpdateTicker) {
      this.boneUpdateTicker = () => {
        if (this.isPlaying && this.isVisible) {
          this.updateCounterFromBone();
          this.updateMultiplierFromBone();
          this.updateButtonFromBone();
        }
      };
      PIXI.Ticker.shared.add(this.boneUpdateTicker);
    }
  }

  /**
   * Stop syncing counter with spine bone
   */
  stopBoneUpdateLoop() {
    if (this.boneUpdateTicker) {
      PIXI.Ticker.shared.remove(this.boneUpdateTicker);
      this.boneUpdateTicker = null;
    }
  }

  /**
   * Update button text based on current device
   */
  updateButtonText() {
    if (!this.buttonText) return;

    // Get translated button text based on current device
    // ── One word, every device: TAP ──
    //
    // This used to say CLICK on desktop. Nothing else in the game did: the
    // intro popup says TAP TO START, the big win says TAP TO CONTINUE
    // ("Always 'TAP TO CONTINUE' regardless of device", in its own words), and
    // Pot Collection's screens say TAP. So the free-spins dialogs were the only
    // ones switching, which made the two screens that bookend the whole feature
    // disagree with the screens either side of them.
    const buttonText = this.t("tap_to_continue").toUpperCase();

    // Update text content
    this.buttonText.text = buttonText;

    // Recalculate font size with device-specific multiplier (matching initialization)
    const baseFontSize = 70;
    const fontMultiplier = this.getFontSizeMultiplier();
    const finalFontSize = baseFontSize * fontMultiplier;

    // Update font size
    this.buttonText.style.fontSize = finalFontSize;
  }

  /**
   * Show continue button with fade-in animation
   */
  showButton() {
    // Show new PIXI button attached to bone
    if (this.buttonContainer) {
      anime({
        targets: this.buttonContainer,
        alpha: 1,
        duration: 300,
        easing: "easeOutQuad",
      });
    }

    // Keep old React button hidden (for backward compatibility)
    if (this.buttonRef?.current) {
      this.buttonRef.current.alpha = 0;
    }
  }

  /**
   * Hide continue button with fade-out animation
   */
  hideButton() {
    // Hide new PIXI button attached to bone
    if (this.buttonContainer) {
      anime({
        targets: this.buttonContainer,
        alpha: 0,
        duration: 300,
        easing: "easeOutQuad",
      });
    }

    // Keep old React button hidden (for backward compatibility)
    if (this.buttonRef?.current) {
      this.buttonRef.current.alpha = 0;
    }
  }

  /**
   * Start the max win animation sequence
   * @param {number} winValue - The win value from state to display
   * @param {number} betValue - The bet value from state to calculate multiplier
   */
  startMaxWinAnimation(winValue = 0, betValue = 0) {
    return new Promise((resolve) => {
      if (this.isPlaying || !this.maxWinSpine?.state) {
        resolve();
        return;
      }

      this.isPlaying = true;
      this.isVisible = true;
      this.isWaitingForClick = false;
      this.winValue = winValue || 0;
      this.betValue = betValue || 0;
      this.overlayRef.current?.fadeInBackground();

      // Reset user interaction guards
      this.hasHandledSkipClick = false;
      this.isHandlingUserClick = false;
      this.canSkipAnimation = false;
      this.isClosing = false;
      this.isFinishing = false;

      // Clear any existing timeouts
      if (this.skipProtectionTimeout) {
        this.skipProtectionTimeout.clear();
        this.skipProtectionTimeout = null;
      }
      if (this.debounceTimeout) {
        clearTimeout(this.debounceTimeout);
        this.debounceTimeout = null;
      }

      // Stop feature_background_music when max win dialog appears
      soundManager.fadeOut(SOUND_NAME.feature_background_music, 500);

      // Play max_win_in sound when animation starts
      soundManager.play(SOUND_NAME.max_win_in);

      // Ensure final positioning
      if (
        this.maxWinSpine &&
        this.symbolHeight &&
        this.symbolHeight > 0 &&
        this.dimensions
      ) {
        this.applyScaleAndCenter();
      }

      // Fade in spine smoothly
      anime({
        targets: this.maxWinSpine,
        alpha: [0.001, 1],
        duration: 350,
        easing: "easeOutQuad",
      });

      this.maxWinSpine.state.clearTracks();
      this.maxWinSpine.state.clearListeners();
      this.maxWinSpine.state.apply(this.maxWinSpine.skeleton);
      this.maxWinSpine.skeleton.setToSetupPose();

      // Reset counter and multiplier to 0 before starting animation
      this.resetCounter();
      this.resetMultiplier();

      // Update counter and multiplier positions immediately
      this.updateCounterFromBone();
      this.updateMultiplierFromBone();

      // Show counter and multiplier, start bone update loop
      this.showCounter();
      this.showMultiplier();
      this.startBoneUpdateLoop();

      // Calculate target multiplier value (max win is always 5000x bet)
      const targetMultiplier = 5000;

      // Start counter and multiplier animations from 0 to target values over 3 seconds
      if (this.winValue > 0) {
        // Animate counter from 0 to final value
        this.startCounterAnimation(0, this.winValue, 3000, () => {
          // Counter animation completed - enable close functionality
          if (this.isPlaying) {
            this.isWaitingForClick = true;
          }
        });

        // Start multiplier animation in parallel with counter
        this.startMultiplierAnimation(0, targetMultiplier, 3000);
      } else {
        // No win value - enable close immediately
        this.isWaitingForClick = true;
      }

      // SKIP PROTECTION: Block skipping for the first second
      // Show button after 1 second
      this.skipProtectionTimeout = new TickerTimeout(() => {
        if (this.isPlaying) {
          this.canSkipAnimation = true;
          this.showButton();

          // Enable waiting if counter already finished or no counter
          if (!this.isCounterAnimating() || this.winValue === 0) {
            this.isWaitingForClick = true;
          }
        }
      }, 1000);

      const animations = this.getAvailableAnimations();
      const inAnimation = animations.find((a) => a === "in");
      const idleAnimation = animations.find((a) => a === "idle");

      if (inAnimation && idleAnimation) {
        // Play IN animation, then loop IDLE animation
        this.maxWinSpine.state.setAnimation(0, inAnimation, false);

        // Get IN animation duration
        const inDuration = this.getSpineAnimationDuration(inAnimation);

        // After IN completes, start looping IDLE
        const inTimeout = new TickerTimeout(() => {
          if (this.isPlaying) {
            // Stop max_win_in and start looping max_win_music
            soundManager.stop(SOUND_NAME.max_win_in);
            soundManager.play(SOUND_NAME.max_win_loop);

            this.maxWinSpine.state.setAnimation(0, idleAnimation, true);
            resolve();
          }
        }, inDuration);

        // Store timeout for cleanup
        this.currentTimeout = inTimeout;
      } else if (idleAnimation) {
        // Fallback: just loop idle animation
        this.maxWinSpine.state.setAnimation(0, idleAnimation, true);
        resolve();
      } else {
        // No valid animations
        resolve();
      }
    });
  }

  /**
   * Get animation duration from spine data
   */
  getSpineAnimationDuration(animationName) {
    if (
      !this.maxWinSpine ||
      !this.maxWinSpine.skeleton ||
      !this.maxWinSpine.skeleton.data ||
      !this.maxWinSpine.skeleton.data.animations
    ) {
      return 1000; // Default fallback
    }

    try {
      const animation = this.maxWinSpine.skeleton.data.animations.find(
        (anim) => anim.name === animationName
      );

      if (animation && animation.duration) {
        const durationMs = Math.round(animation.duration * 1000);
        return durationMs;
      }

      return 1000; // Default fallback
    } catch (error) {
      return 1000; // Default fallback
    }
  }

  /**
   * Handle user click to skip counter or close animation
   */
  handleUserClick() {
    // Basic validation
    if (!this.isPlaying || !this.maxWinSpine?.state) {
      return false;
    }

    // Block interactions during closing or finishing phases
    if (this.isClosing || this.isFinishing) {
      return false;
    }

    // CRITICAL: If debounce timeout exists, block all clicks (prevents timeout buildup)
    // This ensures only ONE click is processed, all others are ignored
    if (this.debounceTimeout) {
      return false;
    }

    // FIRST CLICK ONLY: Create debounce timeout to block subsequent clicks
    // This timeout will be the ONLY timeout created, no matter how many clicks
    this.debounceTimeout = setTimeout(() => {
      this.debounceTimeout = null;
    }, 300);

    // Skip protection: block input for first second of animation
    if (!this.canSkipAnimation) {
      return false;
    }

    // Check if counter is currently animating
    const isCounterAnimating = this.isCounterAnimating();
    const currentCounterValue = this.getCurrentCounterValue();
    const isCounterFinished = currentCounterValue >= this.winValue;

    // If counter is animating, skip it; if finished, close animation
    if (isCounterAnimating && !isCounterFinished) {
      // Counter is still animating - skip to final
      this.hasHandledSkipClick = true;

      // Jump counter and multiplier to final values immediately
      this.jumpCounterToValue(this.winValue);
      const targetMultiplier = 5000;
      this.jumpMultiplierToValue(targetMultiplier);

      // Enable waiting for close click
      this.isWaitingForClick = true;

      return true;
    } else {
      // Counter is finished or not animating - close animation
      if (!this.hasHandledSkipClick && isCounterAnimating) {
        // Counter still animating but skip protection passed - allow skip
        this.hasHandledSkipClick = true;
        this.jumpCounterToValue(this.winValue);
        const targetMultiplier = 5000;
        this.jumpMultiplierToValue(targetMultiplier);
        this.isWaitingForClick = true;

        return true;
      }

      // Counter finished - close animation
      if (!this.isWaitingForClick) {
        // Counter finished naturally, enable close
        this.isWaitingForClick = true;

        return true;
      }

      // Close the animation
      this.playClosingAnimation();

      return true;
    }
  }

  /**
   * Play closing out animation when user clicks to continue
   */
  playClosingAnimation() {
    if (!this.isWaitingForClick || !this.maxWinSpine?.state) return;

    this.isClosing = true;
    this.isWaitingForClick = false;
    this.hideButton();

    // Stop counter and multiplier animations if still running
    if (this.counterAnimationRef) {
      this.counterAnimationRef.pause();
      this.counterAnimationRef = null;
      soundManager.stop(SOUND_NAME.counter);
    }

    if (this.multiplierAnimationRef) {
      this.multiplierAnimationRef.pause();
      this.multiplierAnimationRef = null;
    }

    // Stop max_win_in and max_win_music, play max_win_out
    soundManager.stop(SOUND_NAME.max_win_in);
    soundManager.stop(SOUND_NAME.max_win_loop);
    soundManager.play(SOUND_NAME.max_win_out);

    const animations = this.getAvailableAnimations();
    const outAnimation = animations.find((a) => a === "out");

    this.maxWinSpine.state.clearListeners();

    if (outAnimation) {
      this.maxWinSpine.state.setAnimation(0, outAnimation, false);
      this.maxWinSpine.state.addListener({
        complete: (entry) => {
          if (entry.animation.name === outAnimation && this.isPlaying) {
            this.finishAnimation();
          }
        },
      });
    } else {
      // No out animation, finish immediately
      this.finishAnimation();
    }
  }

  /**
   * Finish the animation and clean up
   */
  finishAnimation() {
    if (!this.maxWinSpine || !this.initialized) return;

    // Set finishing flag immediately to prevent retriggering
    this.isFinishing = true;

    this.isPlaying = false;
    this.isVisible = false;
    this.isWaitingForClick = false;
    this.maxWinSpine.alpha = 0.001;

    // Stop counter and multiplier animations if running
    if (this.counterAnimationRef) {
      this.counterAnimationRef.pause();
      this.counterAnimationRef = null;
    }
    soundManager.stop(SOUND_NAME.counter);

    if (this.multiplierAnimationRef) {
      this.multiplierAnimationRef.pause();
      this.multiplierAnimationRef = null;
    }

    // Stop all max_win sounds
    soundManager.stop(SOUND_NAME.max_win_in);
    soundManager.stop(SOUND_NAME.max_win_loop);
    soundManager.stop(SOUND_NAME.max_win_out);

    // Stop bone update loop and hide counter and multiplier
    this.stopBoneUpdateLoop();
    this.hideCounter();
    this.hideMultiplier();

    // Clear all timeouts
    if (this.currentTimeout) {
      this.currentTimeout.clear();
      this.currentTimeout = null;
    }
    if (this.skipProtectionTimeout) {
      this.skipProtectionTimeout.clear();
      this.skipProtectionTimeout = null;
    }
    if (this.debounceTimeout) {
      clearTimeout(this.debounceTimeout);
      this.debounceTimeout = null;
    }

    // Reset user interaction guards
    this.hasHandledSkipClick = false;
    this.canSkipAnimation = false;
    this.isClosing = false;
    this.isFinishing = false;

    // NOTE: Don't clear completion reload flag here - let scene transition clear it
    // The flag must stay active during scene transition so balance addition can be skipped
    // It will be cleared when setSceneTransitionActive(false) is dispatched at the end
    // NOTE: localStorage completion data is NOT cleared here - it will be cleared when next base game spin happens

    this.dispatch(setSceneTransitionActive(true));

    // Wait for overlay fade out to complete before removing dialog
    this.overlayRef.current?.fadeOutBackground(() => {
      if (this.dispatch) {
        // Close max win dialog
        this.dispatch(setShowMaxWin(false));
      }
    });
  }

  /**
   * Cleanup resources
   */
  cleanup() {
    // Stop bone update loop
    this.stopBoneUpdateLoop();

    // Stop counter and multiplier animations if running
    if (this.counterAnimationRef) {
      this.counterAnimationRef.pause();
      this.counterAnimationRef = null;
    }
    soundManager.stop(SOUND_NAME.counter);

    if (this.multiplierAnimationRef) {
      this.multiplierAnimationRef.pause();
      this.multiplierAnimationRef = null;
    }

    // Stop all max_win sounds
    soundManager.stop(SOUND_NAME.max_win_in);
    soundManager.stop(SOUND_NAME.max_win_loop);
    soundManager.stop(SOUND_NAME.max_win_out);

    // Clear all timeouts
    if (this.currentTimeout) {
      this.currentTimeout.clear();
      this.currentTimeout = null;
    }
    if (this.skipProtectionTimeout) {
      this.skipProtectionTimeout.clear();
      this.skipProtectionTimeout = null;
    }
    if (this.debounceTimeout) {
      clearTimeout(this.debounceTimeout);
      this.debounceTimeout = null;
    }

    // Cleanup counter
    if (this.counterContainer) {
      this.counterBitmapTexts.forEach((text) => text.destroy());
      this.counterBitmapTexts = [];
      if (this.counterContainer.parent) {
        this.counterContainer.parent.removeChild(this.counterContainer);
      }
      this.counterContainer.destroy({ children: true });
      this.counterContainer = null;
    }

    // Cleanup button
    if (this.buttonContainer) {
      if (this.buttonText) {
        this.buttonText.destroy();
        this.buttonText = null;
      }
      this.buttonContainer.destroy({ children: true });
      this.buttonContainer = null;
    }

    // Cleanup multiplier
    if (this.multiplierContainer) {
      if (this.multiplierText) {
        this.multiplierText.destroy();
        this.multiplierText = null;
      }
      this.multiplierContainer.destroy({ children: true });
      this.multiplierContainer = null;
    }

    if (this.maxWinSpine) {
      this.maxWinSpine.state?.clearListeners();
      this.maxWinSpine.state?.clearTracks();
      if (this.maxWinSpine.state) this.maxWinSpine.state.timeScale = 1.0;

      if (this.container?.children.includes(this.maxWinSpine)) {
        this.container.removeChild(this.maxWinSpine);
      }
      this.maxWinSpine.destroy();
      this.maxWinSpine = null;
    }

    this.initialized = false;
  }
}

export default MaxWinDialogController;
